import React, { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';

import { Button, Collapse } from '@mui/material';

import useStyles from './styles';

interface Props {
    maxSize?: number;
    inverted?: boolean;
    className?: string;
    collapseClassName?: string;
}

const TOLERANCE = 10;

const Collapsible: React.FC<PropsWithChildren<Props>> = ({
    className,
    collapseClassName,
    maxSize = 0,
    inverted,
    children
}) => {
    const { classes, cx } = useStyles();
    const collapseRef = useRef<HTMLDivElement | null>();

    const [expanded, setExpanded] = useState(false);
    const toggleExpanded = useCallback(() => setExpanded((state) => !state), []);
    const [isHidden, setIsHidden] = useState(true);
    const [size, setSize] = useState(maxSize);

    useEffect(() => {
        const wrapper = collapseRef.current?.firstElementChild;
        if (wrapper) {
            setIsHidden(wrapper.clientHeight <= maxSize + TOLERANCE);
            setSize(Math.min(wrapper.clientHeight, maxSize + TOLERANCE));
        }
    }, [maxSize]);

    return (
        <div className={cx(classes.root, className)}>
            <Collapse
                in={expanded}
                collapsedSize={size}
                ref={collapseRef}
                className={cx(classes.collapse, collapseClassName)}
            >
                {children}
            </Collapse>
            <Button
                variant="contained"
                onClick={toggleExpanded}
                className={cx(classes.button, { [classes._hidden]: isHidden, [classes._inverted]: inverted })}
                disableElevation
                disableRipple
            >
                {expanded ? 'Show less' : 'Show more'}
            </Button>
        </div>
    );
};

export default Collapsible;
